import {
  collection, doc, getDoc, getDocs, setDoc, addDoc, updateDoc, deleteDoc,
  query, where, orderBy, onSnapshot, arrayUnion, arrayRemove
} from 'firebase/firestore';
import { db, functions } from './firebase';
import { httpsCallable } from 'firebase/functions';

// ==================== AI FEATURES (Cloud Functions) ====================

export const verifyRecaptcha = async (token, action) => {
  const fn = httpsCallable(functions, 'verifyRecaptcha');
  const result = await fn({ token, action });
  return result.data;
};

export const getCoachingTips = async (data) => {
  const fn = httpsCallable(functions, 'getCoachingTips');
  const result = await fn(data);
  return result.data;
};

export const getRecommendedCourses = async (data) => {
  const fn = httpsCallable(functions, 'getRecommendedCourses');
  const result = await fn(data);
  return result.data;
};

export const searchCourses = async (data) => {
  const fn = httpsCallable(functions, 'searchCourses');
  const result = await fn(data);
  return result.data;
};

export const getNearbyCourses = async (data) => {
  const fn = httpsCallable(functions, 'getNearbyCourses');
  const result = await fn(data);
  return result.data;
};

// ==================== USERS ====================

export const getUser = async (userId) => {
  const snap = await getDoc(doc(db, 'users', userId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const getUsers = async () => {
  const snap = await getDocs(collection(db, 'users'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getUserByHandle = async (handle) => {
  const snap = await getDocs(query(collection(db, 'users'), where('handle', '==', handle)));
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
};

export const updateUser = async (userId, data) => {
  await updateDoc(doc(db, 'users', userId), data);
};

export const markAlertSeen = async (userId, alertCreatedAt) => {
  await updateDoc(doc(db, 'users', userId), { seenAlerts: arrayUnion(alertCreatedAt) });
};

export const markHandicapPrompted = async (userId, gameId) => {
  await updateDoc(doc(db, 'users', userId), { handicapPromptedGameIds: arrayUnion(gameId) });
};

export const getSuperAdmin = async () => {
  const snap = await getDocs(query(collection(db, 'users'), where('role', '==', 'superadmin')));
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
};

export const toggleFollow = async (currentUserId, targetUserId) => {
  // `following` is the source of truth — followers lists are derived client-side
  // from other users' `following` arrays. We do not write to the target user's
  // doc because users can't update each other's docs under Firestore rules.
  const currentRef = doc(db, 'users', currentUserId);
  const currentSnap = await getDoc(currentRef);
  const isFollowing = currentSnap.data().following?.includes(targetUserId);

  await updateDoc(currentRef, {
    following: isFollowing ? arrayRemove(targetUserId) : arrayUnion(targetUserId),
  });
  return !isFollowing;
};

export const suspendUser = async (userId, reason, duration) => {
  const now = new Date();
  const endDate = duration ? new Date(now.getTime() + duration * 24 * 60 * 60 * 1000) : null;
  await updateDoc(doc(db, 'users', userId), {
    status: 'suspended',
    suspensionReason: reason,
    suspensionDate: now.toISOString(),
    suspensionDuration: duration || 'permanent',
    suspensionEndDate: endDate ? endDate.toISOString() : 'permanent',
  });
};

export const unsuspendUser = async (userId) => {
  await updateDoc(doc(db, 'users', userId), {
    status: 'active',
    suspensionReason: '',
    suspensionDate: '',
    suspensionDuration: 0,
    suspensionEndDate: '',
  });
};

export const banUser = async (userId, reason) => {
  await updateDoc(doc(db, 'users', userId), {
    status: 'banned',
    banReason: reason,
    banDate: new Date().toISOString(),
  });
};

export const unbanUser = async (userId) => {
  await updateDoc(doc(db, 'users', userId), {
    status: 'active',
    banReason: '',
    banDate: '',
  });
};

export const changeUserRole = async (userId, newRole) => {
  await updateDoc(doc(db, 'users', userId), { role: newRole });
};

export const freezeUser = async (userId, reason) => {
  await updateDoc(doc(db, 'users', userId), {
    status: 'frozen',
    freezeReason: reason,
  });
};

export const unfreezeUser = async (userId) => {
  await updateDoc(doc(db, 'users', userId), {
    status: 'active',
    freezeReason: '',
  });
};

export const blockUser = async (currentUserId, targetUserId) => {
  await updateDoc(doc(db, 'users', currentUserId), {
    blockedUsers: arrayUnion(targetUserId),
  });
};

export const unblockUser = async (currentUserId, targetUserId) => {
  await updateDoc(doc(db, 'users', currentUserId), {
    blockedUsers: arrayRemove(targetUserId),
  });
};

// ==================== POSTS ====================

export const createPost = async (postData) => {
  const docRef = await addDoc(collection(db, 'posts'), {
    ...postData,
    timestamp: new Date().toISOString(),
    likes: [],
    comments: [],
    shares: 0,
    saves: [],
  });
  return { id: docRef.id, ...postData };
};

export const getPosts = async () => {
  const snap = await getDocs(query(collection(db, 'posts'), orderBy('timestamp', 'desc')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const updatePost = async (postId, data) => {
  await updateDoc(doc(db, 'posts', postId), data);
};

export const deletePost = async (postId) => {
  await deleteDoc(doc(db, 'posts', postId));
};

export const likePost = async (postId, userId) => {
  const postRef = doc(db, 'posts', postId);
  const postSnap = await getDoc(postRef);
  const likes = postSnap.data().likes || [];
  const isLiked = likes.includes(userId);

  await updateDoc(postRef, {
    likes: isLiked ? arrayRemove(userId) : arrayUnion(userId),
  });
  return !isLiked;
};

export const savePost = async (postId, userId) => {
  const postRef = doc(db, 'posts', postId);
  const postSnap = await getDoc(postRef);
  const saves = postSnap.data().saves || [];
  const isSaved = saves.includes(userId);

  await updateDoc(postRef, {
    saves: isSaved ? arrayRemove(userId) : arrayUnion(userId),
  });
  return !isSaved;
};

export const addComment = async (postId, comment) => {
  await updateDoc(doc(db, 'posts', postId), {
    comments: arrayUnion({
      id: 'c' + Date.now(),
      ...comment,
      timestamp: new Date().toISOString(),
    }),
  });
};

export const onPostsChange = (callback) => {
  return onSnapshot(
    query(collection(db, 'posts'), orderBy('timestamp', 'desc')),
    (snap) => {
      const posts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      callback(posts);
    }
  );
};

// ==================== EVENTS ====================

export const createEvent = async (eventData) => {
  const docRef = await addDoc(collection(db, 'events'), {
    ...eventData,
    attendees: eventData.attendees || [],
    timestamp: new Date().toISOString(),
  });
  return { id: docRef.id, ...eventData };
};

export const getEvents = async () => {
  const snap = await getDocs(query(collection(db, 'events'), orderBy('date', 'asc')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getEvent = async (eventId) => {
  const snap = await getDoc(doc(db, 'events', eventId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const updateEvent = async (eventId, data) => {
  await updateDoc(doc(db, 'events', eventId), data);
};

export const deleteEvent = async (eventId) => {
  await deleteDoc(doc(db, 'events', eventId));
};

export const joinEvent = async (eventId, userId) => {
  await updateDoc(doc(db, 'events', eventId), {
    attendees: arrayUnion(userId),
  });
};

export const leaveEvent = async (eventId, userId) => {
  await updateDoc(doc(db, 'events', eventId), {
    attendees: arrayRemove(userId),
  });
};

export const onEventsChange = (callback) => {
  return onSnapshot(
    query(collection(db, 'events'), orderBy('date', 'asc')),
    (snap) => {
      const events = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      callback(events);
    }
  );
};

// ==================== CONVERSATIONS ====================

export const createConversation = async (convData) => {
  const docRef = await addDoc(collection(db, 'conversations'), {
    ...convData,
    lastMessage: convData.lastMessage || '',
    lastMessageTime: convData.lastMessageTime || new Date().toISOString(),
  });
  return { id: docRef.id, ...convData };
};

export const createGroupChat = async (eventId, eventTitle, eventDate, creatorId, attendees) => {
  const name = `${eventTitle} - ${eventDate}`;
  const convData = {
    participants: [creatorId, ...attendees.filter(a => a !== creatorId)],
    lastMessage: 'Group chat created',
    lastMessageTime: new Date().toISOString(),
    isGroup: true,
    name,
    linkedEventId: eventId,
  };
  const docRef = await addDoc(collection(db, 'conversations'), convData);
  return { id: docRef.id, ...convData };
};

export const getConversations = async (userId) => {
  const snap = await getDocs(
    query(collection(db, 'conversations'), where('participants', 'array-contains', userId))
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getConversationByEventId = async (eventId) => {
  const snap = await getDocs(
    query(collection(db, 'conversations'), where('linkedEventId', '==', eventId))
  );
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
};

export const updateConversation = async (convId, data) => {
  await updateDoc(doc(db, 'conversations', convId), data);
};

export const addParticipant = async (convId, userId) => {
  await updateDoc(doc(db, 'conversations', convId), {
    participants: arrayUnion(userId),
  });
};

export const removeParticipant = async (convId, userId) => {
  await updateDoc(doc(db, 'conversations', convId), {
    participants: arrayRemove(userId),
  });
};

export const deleteConversation = async (convId) => {
  await deleteDoc(doc(db, 'conversations', convId));
};

export const archiveConversation = async (convId, userId) => {
  await updateDoc(doc(db, 'conversations', convId), {
    archivedBy: arrayUnion(userId),
  });
};

export const unarchiveConversation = async (convId, userId) => {
  await updateDoc(doc(db, 'conversations', convId), {
    archivedBy: arrayRemove(userId),
  });
};

export const onConversationsChange = (userId, callback) => {
  return onSnapshot(
    query(collection(db, 'conversations'), where('participants', 'array-contains', userId)),
    (snap) => {
      const convs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      callback(convs);
    }
  );
};

// ==================== MESSAGES ====================

export const sendMessage = async (messageData) => {
  const docRef = await addDoc(collection(db, 'messages'), {
    ...messageData,
    timestamp: new Date().toISOString(),
    read: false,
  });

  // Update conversation's last message
  let lastMessageText = messageData.content;
  if (!lastMessageText && messageData.mediaType === 'image') lastMessageText = '📷 Photo';
  if (!lastMessageText && messageData.mediaType === 'video') lastMessageText = '🎥 Video';
  await updateDoc(doc(db, 'conversations', messageData.conversationId), {
    lastMessage: lastMessageText || messageData.content,
    lastMessageTime: new Date().toISOString(),
    lastMessageSenderId: messageData.senderId,
    readBy: [messageData.senderId],
  });

  return { id: docRef.id, ...messageData };
};

export const getMessages = async (conversationId) => {
  const snap = await getDocs(
    query(
      collection(db, 'messages'),
      where('conversationId', '==', conversationId),
      orderBy('timestamp', 'asc')
    )
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const updateMessage = async (messageId, data) => {
  await updateDoc(doc(db, 'messages', messageId), data);
};

export const deleteMessage = async (messageId) => {
  await deleteDoc(doc(db, 'messages', messageId));
};

export const markAsRead = async (messageId) => {
  await updateDoc(doc(db, 'messages', messageId), { read: true });
};

export const markConversationAsRead = async (conversationId, userId) => {
  const snap = await getDocs(
    query(
      collection(db, 'messages'),
      where('conversationId', '==', conversationId),
      where('read', '==', false)
    )
  );
  const updates = snap.docs
    .filter(d => d.data().senderId !== userId)
    .map(d => updateDoc(doc(db, 'messages', d.id), { read: true }));
  // Also mark conversation as read by this user
  updates.push(updateDoc(doc(db, 'conversations', conversationId), { readBy: arrayUnion(userId) }));
  await Promise.all(updates);
};

export const onMessagesChange = (conversationId, callback) => {
  return onSnapshot(
    query(
      collection(db, 'messages'),
      where('conversationId', '==', conversationId),
      orderBy('timestamp', 'asc')
    ),
    (snap) => {
      const msgs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      callback(msgs);
    }
  );
};

// ==================== REPORTS ====================

export const createReport = async (reportData) => {
  const docRef = await addDoc(collection(db, 'reports'), {
    ...reportData,
    status: 'pending',
    timestamp: new Date().toISOString(),
  });
  return { id: docRef.id, ...reportData };
};

export const getReports = async () => {
  const snap = await getDocs(query(collection(db, 'reports'), orderBy('timestamp', 'desc')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const updateReport = async (reportId, data) => {
  await updateDoc(doc(db, 'reports', reportId), data);
};

// ==================== MODERATORS ====================

export const getModerators = async () => {
  const snap = await getDocs(collection(db, 'moderators'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const addModerator = async (modData) => {
  await setDoc(doc(db, 'moderators', modData.userId), modData);
};

export const removeModerator = async (userId) => {
  await deleteDoc(doc(db, 'moderators', userId));
};

// ==================== NOTIFICATIONS ====================

export const createNotification = async (notifData) => {
  const docRef = await addDoc(collection(db, 'notifications'), {
    ...notifData,
    read: false,
    timestamp: new Date().toISOString(),
  });
  return { id: docRef.id, ...notifData };
};

export const getNotifications = async (userId) => {
  const snap = await getDocs(
    query(collection(db, 'notifications'), where('targetUserId', '==', userId), orderBy('timestamp', 'desc'))
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const markNotificationRead = async (notifId) => {
  await updateDoc(doc(db, 'notifications', notifId), { read: true });
};

export const markAllNotificationsRead = async (userId) => {
  const snap = await getDocs(
    query(collection(db, 'notifications'), where('targetUserId', '==', userId), where('read', '==', false))
  );
  const updates = snap.docs.map(d => updateDoc(doc(db, 'notifications', d.id), { read: true }));
  await Promise.all(updates);
};

export const onNotificationsChange = (userId, callback) => {
  return onSnapshot(
    query(collection(db, 'notifications'), where('targetUserId', '==', userId), orderBy('timestamp', 'desc')),
    (snap) => {
      const notifs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      callback(notifs);
    },
    (err) => {
      console.error('Notifications listener error:', err);
    }
  );
};

// ==================== APP CONFIG ====================

export const getAppConfig = async (key) => {
  const snap = await getDoc(doc(db, 'appConfig', key));
  return snap.exists() ? snap.data() : null;
};

export const setAppConfig = async (key, data) => {
  await setDoc(doc(db, 'appConfig', key), data, { merge: true });
};

export const onMaintenanceChange = (callback) => {
  return onSnapshot(doc(db, 'appConfig', 'maintenance'), (snap) => {
    callback(snap.exists() ? snap.data() : null);
  }, () => callback(null));
};

// ==================== WORD FILTERS ====================

export const getWordFilters = async () => {
  const snap = await getDoc(doc(db, 'settings', 'wordFilter'));
  return snap.exists() ? (snap.data().words || []) : [];
};

export const setWordFilters = async (words) => {
  await setDoc(doc(db, 'settings', 'wordFilter'), { words });
};

// ==================== EQUIPMENT ====================

export const addEquipment = async (userId, equipmentData) => {
  const equipment = { id: 'eq' + Date.now(), ...equipmentData, addedDate: new Date().toISOString() };
  await updateDoc(doc(db, 'users', userId), { equipment: arrayUnion(equipment) });
  return equipment;
};

export const removeEquipment = async (userId, equipmentId) => {
  const userSnap = await getDoc(doc(db, 'users', userId));
  const updated = (userSnap.data().equipment || []).filter(eq => eq.id !== equipmentId);
  await updateDoc(doc(db, 'users', userId), { equipment: updated });
};

export const updateEquipment = async (userId, equipmentId, equipmentData) => {
  const userSnap = await getDoc(doc(db, 'users', userId));
  const updated = (userSnap.data().equipment || []).map(eq =>
    eq.id === equipmentId ? { ...eq, ...equipmentData } : eq
  );
  await updateDoc(doc(db, 'users', userId), { equipment: updated });
};

// ==================== GROUPS ====================

export const createGroup = async (groupData) => {
  // Create group chat conversation first
  const convData = {
    participants: groupData.members,
    lastMessage: 'Group created',
    lastMessageTime: new Date().toISOString(),
    isGroup: true,
    name: groupData.name,
    linkedGroupId: null, // will be updated after group creation
  };
  const convRef = await addDoc(collection(db, 'conversations'), convData);

  // Create group document
  const groupDoc = {
    ...groupData,
    groupChatId: convRef.id,
    moderators: [],
    createdAt: new Date().toISOString(),
  };
  const groupRef = await addDoc(collection(db, 'groups'), groupDoc);

  // Update conversation with linked group id
  await updateDoc(convRef, { linkedGroupId: groupRef.id });

  return { id: groupRef.id, ...groupDoc };
};

export const getGroups = async (userId) => {
  const snap = await getDocs(
    query(collection(db, 'groups'), where('members', 'array-contains', userId))
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getGroup = async (groupId) => {
  const snap = await getDoc(doc(db, 'groups', groupId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const updateGroup = async (groupId, data) => {
  await updateDoc(doc(db, 'groups', groupId), data);
};

export const deleteGroup = async (groupId) => {
  // Get group to find linked chat
  const groupSnap = await getDoc(doc(db, 'groups', groupId));
  if (groupSnap.exists() && groupSnap.data().groupChatId) {
    await deleteDoc(doc(db, 'conversations', groupSnap.data().groupChatId));
  }
  await deleteDoc(doc(db, 'groups', groupId));
};

export const addGroupMember = async (groupId, userId) => {
  const groupRef = doc(db, 'groups', groupId);
  await updateDoc(groupRef, { members: arrayUnion(userId), pendingInvites: arrayRemove(userId) });
  // Also add to group chat
  const groupSnap = await getDoc(groupRef);
  if (groupSnap.exists() && groupSnap.data().groupChatId) {
    await updateDoc(doc(db, 'conversations', groupSnap.data().groupChatId), {
      participants: arrayUnion(userId),
    });
  }
};

export const inviteToGroup = async (groupId, userId) => {
  const groupRef = doc(db, 'groups', groupId);
  await updateDoc(groupRef, { pendingInvites: arrayUnion(userId) });
};

export const acceptGroupInvite = async (groupId, userId) => {
  await addGroupMember(groupId, userId);
};

export const declineGroupInvite = async (groupId, userId) => {
  const groupRef = doc(db, 'groups', groupId);
  await updateDoc(groupRef, { pendingInvites: arrayRemove(userId) });
};

export const requestToJoinGroup = async (groupId, userId) => {
  const groupRef = doc(db, 'groups', groupId);
  await updateDoc(groupRef, { joinRequests: arrayUnion(userId) });
};

export const acceptJoinRequest = async (groupId, userId) => {
  await addGroupMember(groupId, userId);
  const groupRef = doc(db, 'groups', groupId);
  await updateDoc(groupRef, { joinRequests: arrayRemove(userId) });
};

export const declineJoinRequest = async (groupId, userId) => {
  const groupRef = doc(db, 'groups', groupId);
  await updateDoc(groupRef, { joinRequests: arrayRemove(userId) });
};

export const getAllGroups = async () => {
  const snap = await getDocs(collection(db, 'groups'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const removeGroupMember = async (groupId, userId) => {
  const groupRef = doc(db, 'groups', groupId);
  await updateDoc(groupRef, { members: arrayRemove(userId), moderators: arrayRemove(userId) });
  // Also remove from group chat
  const groupSnap = await getDoc(groupRef);
  if (groupSnap.exists() && groupSnap.data().groupChatId) {
    await updateDoc(doc(db, 'conversations', groupSnap.data().groupChatId), {
      participants: arrayRemove(userId),
    });
  }
};

export const addGroupModerator = async (groupId, userId) => {
  await updateDoc(doc(db, 'groups', groupId), { moderators: arrayUnion(userId) });
};

export const removeGroupModerator = async (groupId, userId) => {
  await updateDoc(doc(db, 'groups', groupId), { moderators: arrayRemove(userId) });
};

export const onGroupsChange = (userId, callback) => {
  // Listen for groups where user is a member
  const unsub1 = onSnapshot(
    query(collection(db, 'groups'), where('members', 'array-contains', userId)),
    (snap) => {
      const memberGroups = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Also fetch groups where user has a pending invite
      getDocs(query(collection(db, 'groups'), where('pendingInvites', 'array-contains', userId)))
        .then((pendingSnap) => {
          const pendingGroups = pendingSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          // Merge, dedup by id
          const allMap = new Map();
          memberGroups.forEach(g => allMap.set(g.id, g));
          pendingGroups.forEach(g => allMap.set(g.id, g));
          callback(Array.from(allMap.values()));
        })
        .catch(() => callback(memberGroups));
    }
  );
  // Also listen for pending invite changes
  const unsub2 = onSnapshot(
    query(collection(db, 'groups'), where('pendingInvites', 'array-contains', userId)),
    (snap) => {
      const pendingGroups = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      getDocs(query(collection(db, 'groups'), where('members', 'array-contains', userId)))
        .then((memberSnap) => {
          const memberGroups = memberSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          const allMap = new Map();
          memberGroups.forEach(g => allMap.set(g.id, g));
          pendingGroups.forEach(g => allMap.set(g.id, g));
          callback(Array.from(allMap.values()));
        })
        .catch(() => callback(pendingGroups));
    }
  );
  return () => { unsub1(); unsub2(); };
};

// ==================== EVENT INVITES ====================

export const inviteUsersToEvent = async (eventId, userIds) => {
  const eventRef = doc(db, 'events', eventId);
  for (const userId of userIds) {
    await updateDoc(eventRef, { invitedUsers: arrayUnion(userId) });
  }
};

export const inviteGroupToEvent = async (eventId, groupId) => {
  await updateDoc(doc(db, 'events', eventId), { invitedGroups: arrayUnion(groupId) });
};

export const removeEventInvite = async (eventId, userId) => {
  await updateDoc(doc(db, 'events', eventId), { invitedUsers: arrayRemove(userId) });
};

// ==================== GAMES ====================

export const createGame = async (gameData) => {
  const docRef = await addDoc(collection(db, 'games'), {
    ...gameData,
    status: 'active',
    currentHole: 1,
    startedAt: new Date().toISOString(),
  });
  return { id: docRef.id, ...gameData };
};

export const getGame = async (gameId) => {
  const snap = await getDoc(doc(db, 'games', gameId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const updateGame = async (gameId, data) => {
  await updateDoc(doc(db, 'games', gameId), data);
};

export const getEventGames = async (eventId) => {
  const snap = await getDocs(
    query(collection(db, 'games'), where('eventId', '==', eventId))
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const completeGame = async (gameId) => {
  await updateDoc(doc(db, 'games', gameId), {
    status: 'completed',
    completedAt: new Date().toISOString(),
  });
};

export const onGameChange = (gameId, callback) => {
  return onSnapshot(
    doc(db, 'games', gameId),
    (snap) => {
      if (snap.exists()) {
        callback({ id: snap.id, ...snap.data() });
      }
    }
  );
};

export const getUserCompletedGames = async (userId) => {
  const snap = await getDocs(
    query(collection(db, 'games'), where('status', '==', 'completed'))
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }))
    .filter(g => g.players?.some(p => p.userId === userId))
    .sort((a, b) => (b.completedAt || '').localeCompare(a.completedAt || ''));
};

export const pauseGame = async (gameId, currentHole) => {
  await updateDoc(doc(db, 'games', gameId), {
    status: 'in_progress',
    currentHole,
  });
};

export const getUserActiveScorecards = async (userId) => {
  const snap = await getDocs(
    query(collection(db, 'games'), where('createdBy', '==', userId))
  );
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(g => g.source === 'scorecard' && (g.status === 'active' || g.status === 'in_progress'))
    .sort((a, b) => (b.startedAt || '').localeCompare(a.startedAt || ''));
};

export const deleteGame = async (gameId) => {
  await deleteDoc(doc(db, 'games', gameId));
};

// ==================== DISPUTES ====================

export const createDispute = async (disputeData) => {
  const docRef = await addDoc(collection(db, 'disputes'), {
    ...disputeData,
    status: 'pending',
    timestamp: new Date().toISOString(),
    resolvedBy: null,
    resolvedAt: null,
    resolution: null,
  });
  return { id: docRef.id, ...disputeData, status: 'pending' };
};

export const getDisputes = async () => {
  const snap = await getDocs(query(collection(db, 'disputes'), orderBy('timestamp', 'desc')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getUserDisputes = async (userId) => {
  const snap = await getDocs(
    query(collection(db, 'disputes'), where('userId', '==', userId))
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
};

export const updateDispute = async (disputeId, data) => {
  await updateDoc(doc(db, 'disputes', disputeId), data);
};

// ==================== REACTIONS ====================

// ==================== SWINGS ====================

export const createSwing = async (swingData) => {
  const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
  const docRef = await addDoc(collection(db, 'swings'), {
    ...swingData,
    viewedBy: [],
    createdAt: new Date().toISOString(),
    expiresAt,
  });
  return { id: docRef.id, ...swingData, viewedBy: [], expiresAt };
};

export const deleteSwing = async (swingId) => {
  await deleteDoc(doc(db, 'swings', swingId));
};

export const markSwingViewed = async (swingId, userId) => {
  await updateDoc(doc(db, 'swings', swingId), { viewedBy: arrayUnion(userId) });
};

export const onSwingsChange = (callback) => {
  return onSnapshot(
    query(collection(db, 'swings'), orderBy('createdAt', 'desc')),
    (snap) => {
      const now = new Date().toISOString();
      const swings = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(s => s.expiresAt > now);
      callback(swings);
    }
  );
};

// ==================== REACTIONS ====================

export const toggleReaction = async (postId, userId, reactionType) => {
  const postRef = doc(db, 'posts', postId);
  const postSnap = await getDoc(postRef);
  const post = postSnap.data();
  const reactions = post.reactions || {};

  // Collect all known types (existing on post + the one being toggled)
  const allTypes = Array.from(new Set([...Object.keys(reactions), reactionType]));

  // Remove user from all reaction types
  const updated = {};
  let wasOnType = false;
  allTypes.forEach(type => {
    const arr = reactions[type] || [];
    if (type === reactionType && arr.includes(userId)) wasOnType = true;
    updated[type] = arr.filter(id => id !== userId);
  });

  // Add to selected type if wasn't already on it
  if (!wasOnType) {
    updated[reactionType] = [...(updated[reactionType] || []), userId];
  }

  await updateDoc(postRef, { reactions: updated });
  return { reactions: updated, wasRemoved: wasOnType };
};
